import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import ProductsDesktop from "./ProductsDesktop";
import ProductsTablet from "./ProductsTablet";
import ProductsMobile from "./ProductsMobile";

import useWindowDimensions from "../../../../../hooks/useWindowDimensions";
import useStores from "../../../../../hooks/useStores";

import productsData from "../../../definitions/ProductsData";
import {
  getProducts,
  type GetProductsResponse,
  type ProductApiItem,
} from "../../../../../gateway/http/requests/ProductsRequests";
import { setQueryParams } from "../../../../../utils";
import { CorrectorQueryParams, ProductCard } from "./ProductsTypes";

const INITIAL_PRODUCTS = productsData;

const ODONTO_SITE_URL = import.meta.env.VITE_ODONTO_SITE ?? "";

function mapProductsFromApi(
  baseProducts: ProductCard[],
  planos: ProductApiItem[]
): ProductCard[] {
  return baseProducts.map((product) => {
    const plano = planos.find(
      (planoData) => planoData.codigo_plano === product.id
    );

    if (!plano) {
      return product;
    }

    const priceValue = plano.valor_preco_base;
    const [price, decimalPrice] = priceValue.toFixed(2).split(".");

    return {
      ...product,
      fullPrice: priceValue,
      fullPriceYear: priceValue,
      price,
      decimalPrice,
      iof: plano.aliquota_iof,
    };
  });
}

function getCorrectorQueryParams(search: string): CorrectorQueryParams {
  const queryParams = new URLSearchParams(search);

  return {
    ev: queryParams.get("ev"),
    ea: queryParams.get("ea"),
    aa: queryParams.get("aa"),
    np: queryParams.get("np"),
    ep: queryParams.get("ep"),
  };
}

function hasAnyCorrectorParam(params: CorrectorQueryParams) {
  return Boolean(params.ev || params.ea || params.aa || params.np || params.ep);
}

function isSuccessfulStatus(status?: number) {
  return status === 200 || status === 201;
}

function getProductsFromResponse(
  responseData: GetProductsResponse | undefined
): ProductApiItem[] | null {
  if (!Array.isArray(responseData?.retorno)) {
    return null;
  }

  return responseData.retorno;
}

export function Products() {
  const navigate = useNavigate();
  const { search } = useLocation();
  const { width } = useWindowDimensions();
  const { productStore } = useStores();

  const [products, setProducts] = useState<ProductCard[]>(INITIAL_PRODUCTS);

  const correctorQueryParams = useMemo(
    () => getCorrectorQueryParams(search),
    [search]
  );

  const ActiveProductsComponent = useMemo(() => {
    if (width > 1400) {
      return ProductsDesktop;
    }

    if (width > 475) {
      return ProductsTablet;
    }

    return ProductsMobile;
  }, [width]);

  useEffect(() => {
    let isMounted = true;

    async function defineProducts() {
      try {
        const response = await getProducts("430");

        if (!isMounted) {
          return;
        }

        if (!isSuccessfulStatus(response?.status)) {
          return;
        }

        const planos = getProductsFromResponse(response?.data);

        if (!planos) {
          return;
        }

        setProducts(mapProductsFromApi(INITIAL_PRODUCTS, planos));
      } catch (error) {
        console.error("Erro ao buscar produtos:", error);
      }
    }

    defineProducts();

    return () => {
      isMounted = false;
    };
  }, []);

  const handleNavigate = useCallback(
    (id: number) => {
      productStore.setId(id);

      if (hasAnyCorrectorParam(correctorQueryParams)) {
        navigate(setQueryParams("/produto/empresa", correctorQueryParams));
        return;
      }

      navigate("/produto");
    },
    [correctorQueryParams, navigate, productStore]
  );

  const navigateOut = useCallback(() => {
    const targetUrl = hasAnyCorrectorParam(correctorQueryParams)
      ? setQueryParams(ODONTO_SITE_URL, correctorQueryParams)
      : ODONTO_SITE_URL;

    window.open(targetUrl, "_blank", "noopener,noreferrer");
  }, [correctorQueryParams]);

  return (
    <ActiveProductsComponent
      products={products}
      navigate={handleNavigate}
      navigateOut={navigateOut}
    />
  );
}

export default Products;